import { DeleteFilled, PlusOutlined } from "@ant-design/icons";
import { Button, Form, FormInstance, FormListFieldData, Input, Modal, Table, TableColumnType } from "antd";
import Tooltip from "components/atoms/Tooltip";
import { ReactNode, useMemo, useRef, useState } from "react";
import scrollIntoView from "scroll-into-view";
import DataTable from "../DataTable";

interface IStaticPickListModalProps {
    formRef?: React.RefObject<FormInstance<ISchemaModel>>;
    fields: FormListFieldData[];
    fieldErrors?: ReactNode[];
    readOnly?: boolean;
    visible: boolean;
    maxLength: number;
    onAddRow?: () => void;
    onRemoveRow?: (index: number) => void;
    onCreate?: () => void;
    onCancel: () => void;
}

const StaticPickListModal = ({ formRef, fields, fieldErrors, readOnly, visible, maxLength, onAddRow, onRemoveRow, onCreate, onCancel }: IStaticPickListModalProps) => {
    const [scrollToLastRow, setScrollToLastRow] = useState(false);
    const codeInputRef = useRef<HTMLInputElement | null>(null);

    const columns: TableColumnType<FormListFieldData>[] = useMemo(() => {
        const _cols: TableColumnType<FormListFieldData>[] = [
            {
                width: "30px",
                title: "No.",
                key: "index",
                render: (value, field, index) => {
                    return <>{index + 1}</>;
                },
            },
            {
                width: "250px",
                title: "Code",
                dataIndex: "code",
                render: (value, field, index) => {
                    return formRef ? (
                        <Form.Item {...field} name={[field.name, "code"]} validateTrigger={["onChange", "onBlur"]} rules={[{ required: true, message: "Code cannot be empty" }]}>
                            <Input
                                readOnly={readOnly}
                                ref={(element) => {
                                    if (element && element.input && scrollToLastRow && index === fields.length - 1) {
                                        if (codeInputRef.current !== element.input) {
                                            scrollIntoView(element.input);
                                            element.input.focus();
                                            codeInputRef.current = element.input;
                                        }
                                    }
                                }}
                            />
                        </Form.Item>
                    ) : (
                        <>{value}</>
                    );
                },
            },
            {
                title: "Description",
                dataIndex: "description",
                render: (value, field, index) => {
                    return formRef ? (
                        <Form.Item {...field} name={[field.name, "description"]}>
                            <Input readOnly={readOnly}></Input>
                        </Form.Item>
                    ) : (
                        <>{value}</>
                    );
                },
            },
        ];

        if (onRemoveRow) {
            _cols.push({
                width: "50px",
                render: (value, field, index) => {
                    return (
                        <Button
                            type="text"
                            aria-label="Remove Item"
                            disabled={readOnly}
                            icon={<DeleteFilled />}
                            danger
                            onClick={() => {
                                onRemoveRow(field.name);
                            }}
                        />
                    );
                },
            });
        }

        return _cols;
    }, [fields.length, formRef, onRemoveRow, readOnly, scrollToLastRow]);

    return (
        <Modal
            open={visible}
            forceRender={true}
            styles={{
                body: { height: "50vh" },
            }}
            centered={true}
            title="Static Picklist"
            width={{
                xs: "90%",
                sm: "90%",
                md: "90%",
                lg: "800px",
            }}
            onCancel={onCancel}
            footer={null}
        >
            <DataTable
                hideSelectionColumn
                emptyText="No picklist item"
                footer={
                    <div className="flex w-full items-center justify-between gap-4 pt-2">
                        <Button type="default" disabled={readOnly} onClick={onCancel}>
                            Cancel
                        </Button>
                        <div className="flex-1" />
                        {fieldErrors && fieldErrors.length > 0 && (
                            <Form.Item>
                                <Form.ErrorList errors={fieldErrors} />
                            </Form.Item>
                        )}
                        {!readOnly && (
                            <>
                                {fields.length > 0 ? (
                                    <Button type="primary" disabled={readOnly} onClick={() => (onCreate ? onCreate() : onCancel())}>
                                        {formRef ? "Save Changes" : "Ok"}
                                    </Button>
                                ) : (
                                    onAddRow && (
                                        <Tooltip title={fields.length >= maxLength ? "Maximum items reached" : null}>
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    setScrollToLastRow(true);
                                                    onAddRow();
                                                }}
                                                disabled={fields.length >= maxLength}
                                                icon={<PlusOutlined />}
                                            >
                                                Add Item
                                            </Button>
                                        </Tooltip>
                                    )
                                )}
                            </>
                        )}
                    </div>
                }
                tableProps={{
                    size: "small",
                    dataSource: fields,
                    className: "form-table",
                    columns: columns,
                    summary: () =>
                        !readOnly && (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={5}>
                                        <Button type="dashed" block onClick={() => onAddRow && onAddRow()} disabled={readOnly} icon={<PlusOutlined />}>
                                            Add More
                                        </Button>
                                        <div className="h-20" />
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        ),
                }}
            />
        </Modal>
    );
};

export default StaticPickListModal;
